import { area, curveCardinal } from 'd3-shape';
import { getUserAgent } from '../CommonJs/url';
import { pushDataLayerEvent } from '../CommonJs/dataLayer.js';

const page_type_graph = 'graph';
const page_type_radar = 'radar';
const page_type_weather = 'weather';

const contentGroupMap = {
    graph: { group: 'location', type: 'location/city' },
    radar: { group: 'radar', type: 'radar/location' },
    weather: { group: 'forecast', type: 'forecast/14d' }
}

export class RadarGraph {
    graphData = [];
    graphElement;
    nowTimer;
    refreshTimer;
    currentDay = '2hour';
    mode = 'graph';
    map;
    series;
    timestamps = [];
    lastGraphX = null;

    constructor() {
        this.graphData = weatherData;

        this.graphElement = document.querySelector('.graph');
        this.graphElement.addEventListener('mousemove', this.onGraphMove);
        this.graphElement.addEventListener('touchmove', this.onGraphMove);
    }

    roundTo(value, decimals) {
        return Number(value.toFixed(decimals));
    }

    init() {
        this.setupGraph();
        this.setupTimes();
        this.setupNow();
        this.setupTimers();
        this.setupButtonRow();
        this.setupButtonSwitch();
        this.setupMap();
        this.setStep(this.findNowIndex(), false);
    }

    styleHour = (hours) => {
        if (hours === null || hours === undefined) {
            return;
        }

        let deg = 0;

        if (hours) {
            const hour = parseInt(hours, 10) % 12;
            deg = hour * 30;
        }

        return `rotate(${deg}deg)`;
    }

    styleMinute = (minutes) => {
        if (minutes === null || minutes === undefined) {
            return;
        }

        let deg = 0;

        if (minutes) {
            const minute = parseInt(minutes, 10);
            deg = minute * 6;
        }

        return `rotate(${deg}deg)`;
    }

    setClock = (date) => {
        if (!date) {
            return;
        }

        const clockElement = document.querySelector('.clock');
        const hourElement = clockElement.querySelector('.hour');
        const minuteElement = clockElement.querySelector('.minute');

        hourElement.style.transform = this.styleHour(date.getHours());
        minuteElement.style.transform = this.styleMinute(date.getMinutes());
    }

    updateUrl = (mode) => {
        const currentMode = mode || this.mode;
        const url = new URL(window.location.href);

        // Do not change url for graph
        if (currentMode === page_type_graph) {
            if (url.searchParams.has('mode')) {
                url.searchParams.delete('mode');

                pushDataLayerEvent({
                    content_group: contentGroupMap[page_type_graph].group,
                    content_type: contentGroupMap[page_type_graph].type
                });

                window.history.replaceState({}, '', url.toString());
                
                return;
            }
        }

        const oldMode = url.searchParams.get('mode');

        if (url.searchParams.has('mode')) {
            url.searchParams.set('mode', currentMode);
        } else {
            url.searchParams.append('mode', currentMode);
        }

        if (oldMode !== currentMode) {
            pushDataLayerEvent({
                content_group: contentGroupMap[currentMode].group,
                content_type: contentGroupMap[currentMode].type
            });

            window.history.replaceState({}, '', url.toString());
        }
    }

    setupButtonRow = async () => {
        const buttonRow = document.querySelectorAll('.button-row .button');

        buttonRow[0].classList.add('active');
        
        buttonRow[0].addEventListener('click', () => {
            this.mode = page_type_graph;
            buttonRow[0].classList.add('active');
            buttonRow[1].classList.remove('active');
            this.updateUrl();
            this.switchMode();
        });

        buttonRow[1].addEventListener('click', () => {
            this.mode = page_type_radar;
            buttonRow[0].classList.remove('active');
            buttonRow[1].classList.add('active');
            this.updateUrl();
            this.switchMode();

            setTimeout(() => {
                this.resizeMap();
            }, 250);
        });

        // Checks if the button row is visible
        if (!buttonRow || buttonRow.length === 0 || !buttonRow[0].offsetParent) {
            return;
        }
        const weatherElement = document.getElementById('24u');
        if (weatherElement) {
            let scrollTimeout;
            window.addEventListener('scroll', () => {
                clearTimeout(scrollTimeout);
                scrollTimeout = setTimeout(() => {
                    const rect = weatherElement.getBoundingClientRect();
                    const screenHeight = window.innerHeight;
                    if ((rect.top <= screenHeight * 0.6 && rect.bottom >= 0) || rect.top < 0) {
                        this.updateUrl(page_type_weather)
                    } else {
                        this.updateUrl();
                    }
                }, 500);
            });
        }
    }

    setupButtonSwitch = async () => {
        const buttonSwitch = document.querySelectorAll('.button-switch .button');

        buttonSwitch[0].classList.add('active');

        buttonSwitch[0].addEventListener('click', () => {
            buttonSwitch[0].classList.add('active');
            buttonSwitch[1].classList.remove('active');

            this.switchDay('2hour');
        });

        buttonSwitch[1].addEventListener('click', () => {
            buttonSwitch[0].classList.remove('active');
            buttonSwitch[1].classList.add('active');

            this.switchDay('24hour');
        });
    }

    setupMap = () => {
        this.map = IPT.map('map', {
            center: [siteCurrentArea.latitude, siteCurrentArea.longitude],
            zoom: 7,
            theme: IPT.THEME.DARK,
            mapLayers: [{
                tileUrl: 'https://maps.meteoplaza.com/styles/buienalarmi8n/{z}/{x}/{y}.png',
                tileJson: 'https://api.meteoplaza.com/buienalarmi8n/v1/maps/default/nl',
                zIndex: 10
            }]
        });

        IPT.ui.location([siteCurrentArea.latitude, siteCurrentArea.longitude], {
            color: 'orange',
            active: true,
            draggable: false,
            continue: false
        }).addTo(this.map);

        let forecast;

        if (['NL', 'BE', 'DE'].includes(siteCurrentArea.country_code)) {
            forecast = Object.assign({}, IPT.NOWCAST.RADAR_FORECAST_3H);
        } else {
            forecast = Object.assign({}, IPT.NOWCAST.RADAR_FORECAST);
        }

        forecast.palette = 'buienalarm';

        this.series = this.map.setElementLayers(forecast, {
            onLoad: (timestamps) => {
                this.timestamps = timestamps;
                queueMicrotask(() => this.seekMapToNow());
            },
            onChange: (timestamp) => {
                this.mapOnChange(timestamp);
            },
            timebar: { autoplay: true }
        });

        window.addEventListener('positionStepChanged', (event) => {
            this.series.stop();

            if (!this.timestamps.length) {
                return;
            }

            const date = new Date(event.detail);
            const newIndex = this.timestamps.indexOf(date.getTime() / 1000);

            if (newIndex > -1) {
                this.series.setIndex(newIndex);
            }
        });

        const pauseButton = document.querySelector('.leaflet-bar .ip-btn');

        pauseButton.addEventListener('click', () => {
            // Check if ip-active class is present
            if (pauseButton.classList.contains('ip-active')) {
                pushDataLayerEvent({
                    event: 'radar_interaction',
                    click_name: 'play'  
                });
            } else {
                pushDataLayerEvent({
                    event: 'radar_interaction',
                    click_name: 'pause'
                });
            }
        });

        const button = document.querySelector('.map-legend button');

        button.addEventListener('click', () => {
            const legend = document.querySelector('.map-legend-content');
            legend.classList.toggle('hidden');
        });
    }

    resizeMap = () => {
        if (this.map) {
            this.map.invalidateSize();

            // Zoom in one level to make the map look better on mobile
            this.map.setZoom(this.map.getZoom() + 1);
        }
    }

    setupGraph = () => {
        const height = 200;
        const rain = [];
        let dry = false;

        if (this.graphData.length === 0) {
            dry = true;
        }

        for (let i = 0; i < this.graphData.length; i++) {
            rain.push(this.graphData[i].rain);

            if (!dry && this.graphData[i].rain > 0) {
                dry = false;
            }
        }

        // TODO show or hide the text "No rain expected" based on the value of dry

        let maximumRain = Math.max(...rain) * 1.025;

        if (maximumRain < 3) {
            maximumRain = 3;
        }

        maximumRain = Math.ceil(maximumRain);

        const lineHeavy = document.querySelector('.graph-line-heavy');
        const lineModerate = document.querySelector('.graph-line-moderate');
        const lineLight = document.querySelector('.graph-line-light');

        lineHeavy.style.bottom = ((100 / maximumRain) * 2.5) + '%';
        lineModerate.style.bottom = ((100 / maximumRain) * 1.0) + '%';
        lineLight.style.bottom = ((100 / maximumRain) * 0.25) + '%';

        const points = [];

        for (let i = 0; i < rain.length; i++) {
            points.push({
                x: (i * 10),
                y: this.roundTo(height - (rain[i] / maximumRain * height), 1)
            });
        }

        const graphingFunction = area()
            .curve(curveCardinal)
            .x(function (d) { return d.x })
            .y0(height)
            .y1(function (d) { return d.y });

        const path = graphingFunction(points);

        const chart = document.querySelector('.graph-svg');
        chart.setAttribute('viewBox', `0 0 ${(points.length - 1) * 10} ${height}`);
        chart.querySelector('path').setAttribute('d', path);

        this.setTopbar(new Date(this.graphData[0].date), this.graphData[0].rain, this.graphData[0].temp);
    }

    setupTimes = () => {
        const dates = [];
        const hours = [];

        for (let i = 0; i < this.graphData.length; i++) {
            const date = new Date(this.graphData[i].date);

            if (date.getMinutes() === 0) {
                hours.push(date);
            }

            dates.push(date);
        }

        const offset = Math.round(hours.length / 8);
        let offsetHours = [];

        if (offset === 0) {
            offsetHours = hours;
        } else {
            for (let i = 0; i < hours.length; i++) {
                if (i % offset === 0 && i > 0) {
                    offsetHours.push(hours[i]);
                }
            }
        }

        const timebarElement = document.querySelector('.graph-times');
        timebarElement.innerHTML = '';

        const timeStart = dates[0].getTime();
        const timeEnd = dates[dates.length - 1].getTime();

        for (let i = 0; i < offsetHours.length; i++) {
            const time = offsetHours[i].getTime();
            const left = (100 / (timeEnd - timeStart)) * (time - timeStart);
            const hourElement = document.createElement('div');

            hourElement.classList.add('graph-time');
            hourElement.style.left = left + '%';
            hourElement.innerHTML = ('0' + offsetHours[i].getHours()).substr(-2) + ':00';

            timebarElement.appendChild(hourElement);
        }
    }

    setupNow = () => {
        const nowTime = new Date().getTime();
        const nowElement = document.querySelector('.graph-line-now');

        if (this.graphData.length > 0) {
            const timeStart = new Date(this.graphData[0].date).getTime();
            const timeEnd = new Date(this.graphData[this.graphData.length - 1].date).getTime();

            if (nowTime < timeStart || nowTime > timeEnd) {
                nowElement.classList.add('hide');
            } else {
                const left = (100 / (timeEnd - timeStart)) * (nowTime - timeStart);
                nowElement.style.left = left + '%';
                nowElement.classList.remove('hide');
            }
        } else {
            nowElement.classList.add('hide');
        }
    }

    setupTimers = () => {
        this.nowTimer = setInterval(() => {
            this.setupNow();
        }, 60 * 1000);

        this.refreshTimer = setInterval(() => {
            this.switchDay(this.currentDay);
        }, 15 * 60 * 1000);

        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                clearInterval(this.nowTimer);
                clearInterval(this.refreshTimer);
            } else {
                this.setupNow();

                this.nowTimer = setInterval(() => {
                    this.setupNow();
                }, 60 * 1000);

                this.refreshTimer = setInterval(() => {
                    this.switchDay(this.currentDay);
                }, 15 * 60 * 1000);
            }
        });
    }

    setSelector = (index) => {
        const selectorElement = document.querySelector('.graph-line-selector');

        if (this.graphData.length > 0) {
            const left = (100 / (this.graphData.length - 1)) * index;
            selectorElement.style.left = left + '%';
            selectorElement.classList.remove('hide');
        } else {
            selectorElement.classList.add('hide');
        }
    }

    setTopbar = (time, mm, temperature) => {
        const topbarElement = document.querySelector('.graph-topbar');
        const timeElement = topbarElement.querySelector('.time');
        const mmElement = topbarElement.querySelector('.mm');
        const temperatureElement = topbarElement.querySelector('.temp');

        timeElement.textContent = time.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
        mmElement.textContent = `${mm} mm/u`;
        temperatureElement.textContent = `${temperature}°`;
    }

    setStep = (index, sendEvent = true) => {
        const timestep = this.graphData[index];

        this.setTopbar(new Date(timestep.date), timestep.rain, timestep.temp);
        this.setClock(new Date(timestep.date));
        this.setSelector(index);

        if (sendEvent) {
            const event = new CustomEvent('positionStepChanged', { detail: timestep.date });
            window.dispatchEvent(event);
        }
    }

    switchMode = () => {
        const graphWrapper = document.querySelector('.graph-wrapper');
        const map = document.querySelector('.map');

        // Radar + short window: split layout (compact graph + map). Otherwise full-height graph;
        // hide the map only when not on the 2hour/3h series (switchDay owns map visibility for 2hour).
        if (this.mode === 'radar' && this.currentDay === '2hour') {
            graphWrapper.classList.remove('graph-tall');
            map.classList.remove('hidden');

            setTimeout(() => {
                this.resizeMap();
            }, 250);
        } else {
            graphWrapper.classList.add('graph-tall');
            if (this.currentDay !== '2hour') {
                map.classList.add('hidden');
            }
        }
    }

    switchDay = async (day) => {
        try {
            this.currentDay = day;
            const oldDayElement = document.querySelector('.graph-day.active');

            if (oldDayElement) {
                oldDayElement.classList.remove('active');
            }

            const dayElement = document.querySelectorAll('[data-day="' + day + '"]');

            dayElement[0].classList.add('active');

            const res = await fetch(`/api/weather/splashid?type=${day}&location_id=${siteCurrentArea.location_id}`, {
                headers: {
                    'User-Agent': getUserAgent()
                }
            });

            if (!res.ok) {
                throw new Error('Kon de data niet ophalen. Probeer het later opnieuw.');
            }

            const data = await res.json();
            this.graphData = data;

            this.setupGraph();
            this.setupTimes();
            this.setupNow();

            this.setStep(day === '2hour' ? this.findNowIndex() : 0, this.mode === page_type_radar);

            const map = document.querySelector('.map');
            const left = document.querySelector('.left');

            if (day === '2hour') {
                map.classList.add('md:flex');
                map.classList.remove('hidden');
                left.classList.remove('full');

                pushDataLayerEvent({
                    event: 'buienplanner_click',
                    click_name: '3_hours'
                });
            } else if (day === '24hour') {
                map.classList.remove('md:flex');
                map.classList.add('hidden');
                left.classList.add('full');

                pushDataLayerEvent({
                    event: 'buienplanner_click',
                    click_name: '24_hours'
                });
            } else {
                map.classList.remove('md:flex');
                map.classList.add('hidden');
                left.classList.add('full');

                // Get index of the day element
                const dayIndex = dayElement[0].getAttribute('data-index');

                pushDataLayerEvent({
                    event: 'buienplanner_click',
                    click_name: `${dayIndex}_days`
                });
            }

            this.switchMode();
        } catch (error) {
            console.log(error);
        }
    }

    onGraphMove = (event) => {
        if (this.graphData.length > 0) {
            const elWidth = event.currentTarget.clientWidth;
            const elLeft = event.currentTarget.getBoundingClientRect().left;
            const posX = (event.type.indexOf('mouse') === 0 ? event.clientX : event.touches[0].pageX) - elLeft;

            if (posX >= 0 && posX < elWidth + elLeft) {
                const delta = elWidth / this.graphData.length;
                let index = Math.floor(posX / delta);

                if (index > this.graphData.length - 1) {
                    index = this.graphData.length - 1;
                }

                this.setStep(index, true);

                this.lastGraphX = posX;
            }
        }
    }

    getTimestamp = (date) => {
        return Math.floor(new Date(date).getTime() / 1000);
    }

    findNowIndex = () => {
        if (!this.graphData.length) {
            return 0;
        }

        const now = Date.now();
        let best = 0;
        let bestDelta = Infinity;

        for (let i = 0; i < this.graphData.length; i++) {
            const delta = Math.abs(new Date(this.graphData[i].date).getTime() - now);
            if (delta < bestDelta) {
                bestDelta = delta;
                best = i;
            }
        }

        return best;
    }

    seekMapToNow = () => {
        if (!this.series || !this.timestamps.length) {
            return;
        }

        const nowTs = Math.floor(Date.now() / 1000);
        let index = this.timestamps.findIndex((timestamp) => timestamp >= nowTs);

        if (index < 0) {
            index = this.timestamps.length - 1;
        }

        this.series.setIndex(index);
    }

    mapOnChange = (timestamp) => {
        if (!timestamp || this.graphData.length === 0) {
            return;
        }

        if (this.currentDay !== '2hour') {
            return;
        }

        const index = this.graphData.findIndex(x => this.getTimestamp(x.date) === timestamp);

        if (index > -1) {
            this.setStep(index, false);
        }
    }
}

document.addEventListener('DOMContentLoaded', () => {
    const defaultInstance = new RadarGraph();
    defaultInstance.init();
    window.RadarGraph = defaultInstance;
});